import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'

import { EditSupplierFormDto, SupplierDetail } from '../data'
import { SupplierView } from './Supplier.view'

export type SupplierFormFieldName = keyof EditSupplierFormDto

type SupplierContainerProps = {
  isLoading: boolean
  isRequestRunning: boolean
  supplier?: SupplierDetail
  formValues: EditSupplierFormDto
  submit: (formData: EditSupplierFormDto) => Promise<void>
  activateSupplier: () => Promise<void>
  deactivateSupplier: () => Promise<void>
}

export const SupplierForm: React.FC<SupplierContainerProps> = ({
  isLoading,
  isRequestRunning,
  supplier,
  formValues,
  submit,
  activateSupplier,
  deactivateSupplier,
}) => {
  const { t } = useTranslation()

  const [isEdit, setIsEdit] = useState(false)
  const [wrongSupportEmails, setWrongSupportEmails] = useState<string[] | null>(
    null,
  )
  const [wrongInboundEmails, setWrongInboundEmails] = useState<string[] | null>(
    null,
  )
  const [wrongOutboundEmails, setWrongOutboundEmails] = useState<
    string[] | null
  >(null)

  const separator = ';'

  const validateEmailField = (
    data: string,
    fieldErrors: FormikErrors<EditSupplierFormDto>,
    fieldName: SupplierFormFieldName,
    setWrongEmails: Function,
  ) => {
    const emailValidationRegex =
      /^[a-zA-Z0-9._%+-]+[a-zA-Z0-9]+@[a-zA-Z0-9]+[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    const emails = data?.split(separator)
    const invalidEmails = emails?.filter(
      email => !emailValidationRegex.test(email),
    )
    if (invalidEmails?.length) {
      setWrongEmails(invalidEmails)
      fieldErrors[fieldName] = t(
        'settings.email.common.errorMessage.wrongEmail',
        { count: invalidEmails?.length },
      )
    } else {
      setWrongEmails(null)
    }
  }

  // formik
  const formik = useFormik<EditSupplierFormDto>({
    enableReinitialize: true,
    initialValues: formValues,
    validate: data => {
      let errors: FormikErrors<EditSupplierFormDto> = {}

      if (!data.name) {
        errors.name = t('common.errors.required')
      }

      if (
        data.orderDeliveryDateDeviationValue &&
        data.orderDeliveryDateDeviationValue < 0
      ) {
        errors.orderDeliveryDateDeviationValue = t('common.errors.negative')
      }

      if (data.supportValue)
        validateEmailField(
          data.supportValue,
          errors,
          'supportValue',
          setWrongSupportEmails,
        )

      if (data.inboundValue)
        validateEmailField(
          data.inboundValue,
          errors,
          'inboundValue',
          setWrongInboundEmails,
        )

      if (data.outboundValue)
        validateEmailField(
          data.outboundValue,
          errors,
          'outboundValue',
          setWrongOutboundEmails,
        )

      return errors
    },
    onSubmit: async (data: EditSupplierFormDto) => {
      try {
        await submit(data)
        setIsEdit(false)
      } catch (error) {
        setIsEdit(true)
      }
    },
  })

  const isFormFieldInvalid = (name: SupplierFormFieldName) =>
    !!(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: SupplierFormFieldName) => {
    return isFormFieldInvalid(name) ? (
      <small className="p-error">{formik.errors[name]}</small>
    ) : (
      <small className="p-error">&nbsp;</small>
    )
  }

  return (
    <SupplierView
      isEdit={isEdit}
      setIsEdit={setIsEdit}
      isLoading={isLoading}
      formik={formik}
      supplier={supplier}
      isFormFieldInvalid={isFormFieldInvalid}
      getFormErrorMessage={getFormErrorMessage}
      wrongSupportEmails={wrongSupportEmails}
      wrongInboundEmails={wrongInboundEmails}
      wrongOutboundEmails={wrongOutboundEmails}
      separator={separator}
      activateSupplier={activateSupplier}
      deactivateSupplier={deactivateSupplier}
      isRequestRunning={isRequestRunning}
    />
  )
}
